import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, ShieldOff, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { matchingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtTimestamp(isoString) {
  if (!isoString) return '-';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return isoString;
  return then.toLocaleString('en-GB');
}

// FR Auto-Finalize status + flag toggle (Diagnostics card): the pipeline's
// finalize step (manual trigger, finalize-apply, and the day-2 scheduled
// job alike) is governed by a single DB-backed flag. Toggling it off is
// immediate and safe (drafts just stay draft); toggling it on is gated
// behind a confirmation dialog since the very next scheduled run could act
// on it. The former separate Arm/Disarm gate and review-mark machinery
// have been retired along with the day=1 job they guarded.
export function AutoFinalizeSchedulerPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await matchingAPI.getAutoFinalizeSchedulerStatus();
      setStatus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runEnable = async () => {
    if (!window.confirm(
      'Enable auto-finalize? The next scheduled run (and any manual trigger or Apply) will ' +
      'finalize eligible invoices. Finalization is irreversible.'
    )) {
      return;
    }
    setBusy(true);
    try {
      const { data } = await matchingAPI.setAutoFinalizeEnabled(true);
      setStatus(data);
      toast.success('Auto-finalize enabled');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const runDisable = async () => {
    setBusy(true);
    try {
      const { data } = await matchingAPI.setAutoFinalizeEnabled(false);
      setStatus(data);
      toast.success('Auto-finalize disabled');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const lastRuns = status?.last_runs ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          {status?.finalize_enabled ? (
            <ShieldCheck className="h-4 w-4 text-amber-500" />
          ) : (
            <ShieldOff className="h-4 w-4 text-muted-foreground" />
          )}
          FR Auto-Finalize
        </CardTitle>
        <CardDescription>
          A single flag governs the finalize step everywhere it can run: the manual trigger,
          the row-level Apply on the Invoicing page, and the monthly scheduled job (2nd of
          each month). Default ON. Disabling it leaves drafts as drafts; it never touches
          generation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="auto-finalize-scheduler-refresh" size="sm" variant="outline" onClick={refresh} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh
          </Button>
          <Button
            data-testid="auto-finalize-scheduler-enable"
            size="sm"
            onClick={runEnable}
            disabled={busy || !status || status.finalize_enabled}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
            Enable
          </Button>
          <Button
            data-testid="auto-finalize-scheduler-disable"
            size="sm"
            variant="outline"
            onClick={runDisable}
            disabled={busy || !status || !status.finalize_enabled}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ShieldOff className="h-4 w-4 mr-1" />}
            Disable
          </Button>
        </div>

        {status && (
          <div className="space-y-3" data-testid="auto-finalize-scheduler-status">
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant={status.finalize_enabled ? 'default' : 'secondary'} data-testid="auto-finalize-scheduler-flag-badge">
                finalize: {status.finalize_enabled ? 'enabled' : 'disabled'}
              </Badge>
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              {status.finalize_enabled && status.enabled_at && (
                <div>Enabled by {status.enabled_by || 'unknown'} at {fmtTimestamp(status.enabled_at)}</div>
              )}
              {!status.finalize_enabled && status.disabled_at && (
                <div>Disabled by {status.disabled_by || 'unknown'} at {fmtTimestamp(status.disabled_at)}</div>
              )}
              <div>Next scheduled run: {fmtTimestamp(status.next_scheduled_run)}</div>
            </div>

            <div className="space-y-1">
              <p className="text-xs font-medium">Last runs</p>
              {lastRuns.length === 0 ? (
                <div className="text-xs text-muted-foreground" data-testid="auto-finalize-scheduler-last-runs-empty">
                  No runs recorded yet.
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-lg max-h-56 overflow-y-auto" data-testid="auto-finalize-scheduler-last-runs">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50 sticky top-0">
                        <th className="text-left px-2 py-1 font-medium">When</th>
                        <th className="text-left px-2 py-1 font-medium">Entity / Period</th>
                        <th className="text-left px-2 py-1 font-medium">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastRuns.map((run, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-2 py-1 whitespace-nowrap">{fmtTimestamp(run.created_at)}</td>
                          <td className="px-2 py-1 whitespace-nowrap">
                            {run.entity_id ? `${run.entity_id} / ${run.period}` : '-'}
                          </td>
                          <td className="px-2 py-1">
                            {run.report?.skipped_reason
                              ? run.report.message || run.report.skipped_reason
                              : `finalized=${run.report?.finalized_count ?? 0} skipped=${run.report?.skipped_count ?? 0} errors=${run.report?.error_count ?? 0}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
